import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/core/auth/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { AppService } from './app.service';
import { RoleCode } from './core/enums';
import { LocalStorageService } from './core/services/local-storage.service';
import { VersionCheckerService } from './core/services/version-checker.service';
import { MultiLanguageService } from './layout/common/language-selector/multi-language.service';
import { ConsentBannerComponent } from './shared/components/organisms/consent-banner';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet, ConsentBannerComponent],
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly _unsubscribeAll = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private readonly _authService: AuthService,
        private readonly _localStorageService: LocalStorageService,
        private readonly _multiLanguageService: MultiLanguageService,
        private readonly _translateService: TranslateService,
        private readonly _versionCheckerService: VersionCheckerService,
        private readonly _appService: AppService,
        private readonly _renderer: Renderer2
    ) {
        const user = JSON.parse(this._localStorageService.get('_user'));

        if (user?.defaultLanguage) {
            this._multiLanguageService.setLanguage(user.defaultLanguage);
            this._updateHtmlLangAttribute(user.defaultLanguage);
        } else {
            const browserLang = this._translateService.getBrowserLang();
            if (browserLang) {
                this._multiLanguageService.setLanguage(browserLang);
                this._updateHtmlLangAttribute(browserLang);
            } else {
                this._multiLanguageService.setLanguage('en');
                this._updateHtmlLangAttribute('en');
            }
        }
    }

    ngOnInit(): void {
        // Initialize subjects after app reload
        this._authService.initializeSubjects();

        // Start version checking to detect new deployments
        this._versionCheckerService.startVersionChecking();

        // Listen to language changes and update HTML lang attribute
        this._multiLanguageService.currentLanguage$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((lang) => {
                this._updateHtmlLangAttribute(lang);
            });

        this.sendDeviceDetailsIfApplicable();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all observables
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    private _updateHtmlLangAttribute(lang: string): void {
        const htmlElement = document.documentElement;
        this._renderer.setAttribute(htmlElement, 'lang', lang);

        // Also add language class for CSS targeting
        this._renderer.removeClass(htmlElement, 'lang-en');
        this._renderer.removeClass(htmlElement, 'lang-ta');
        this._renderer.removeClass(htmlElement, 'lang-ml');
        this._renderer.addClass(htmlElement, `lang-${lang}`);
    }

    private sendDeviceDetailsIfApplicable(): void {
        const isLoggedIn = this._authService.isLoggedIn.value;

        if (!isLoggedIn) {
            return;
        }

        const currentRole = this._authService._getCurrentUserRole();
        if (currentRole !== RoleCode.FILM_MAKER) {
            return;
        }

        this._appService
            .sendDeviceDetails(RoleCode.FILM_MAKER)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (_res) => {
                    // Empty
                },
                error: (error: HttpErrorResponse) => {
                    console.error('Error sending device details:', error);
                },
            });
    }
}
